import React, { FC, useMemo } from 'react';
import styled, { keyframes } from 'styled-components/macro';
import { Co2FootprintVO } from 'vo';
import { useTranslation } from 'react-i18next';
import { ChartDonutSection, ChartSection, TotalFootprintValue } from './components';
import { RedAccentHeading } from 'components';
import { DEVICE, TYPOGRAPHY } from '@net0-calc/common/assets/styles';
import { formatWeightValue } from 'utils';

interface TopContainerProps {
  co2FootprintSumVO: Co2FootprintVO;
  eventName: string;
  co2FootprintsVOs: {
    travelAndAccommodation: Co2FootprintVO;
    hospitality: Co2FootprintVO;
    catering: Co2FootprintVO;
    merchandise: Co2FootprintVO;
    eventDurationAndInfo: Co2FootprintVO;
  };
  co2FootprintPerAttendeVOs: {
    travelAndAccommodation: Co2FootprintVO;
    hospitality: Co2FootprintVO;
    catering: Co2FootprintVO;
    merchandise: Co2FootprintVO;
    eventDurationAndInfo: Co2FootprintVO;
  };
}

export const TopContainer: FC<TopContainerProps> = ({
  eventName,
  co2FootprintSumVO,
  co2FootprintPerAttendeVOs,
  co2FootprintsVOs,
}) => {
  const { t } = useTranslation();

  const totalKg = co2FootprintSumVO.kg;
  const perAttendeeVO = Co2FootprintVO.sum(Object.values(co2FootprintPerAttendeVOs));

  const breakdownItems = useMemo(
    () => [
      {
        color: 'var(--chart-color1)',
        label: t('events.pages.result.topContainer.marks.travelAndAccommodation'),
        value: co2FootprintsVOs.travelAndAccommodation.stringifiedValue,
        kg: co2FootprintsVOs.travelAndAccommodation.kg,
      },
      {
        color: 'var(--chart-color2)',
        label: t('events.pages.result.topContainer.marks.hospitality'),
        value: co2FootprintsVOs.hospitality.stringifiedValue,
        kg: co2FootprintsVOs.hospitality.kg,
      },
      {
        color: 'var(--chart-color3)',
        label: t('events.pages.result.topContainer.marks.catering'),
        value: co2FootprintsVOs.catering.stringifiedValue,
        kg: co2FootprintsVOs.catering.kg,
      },
      {
        color: 'var(--chart-color4)',
        label: t('events.pages.result.topContainer.marks.merchandise'),
        value: co2FootprintsVOs.merchandise.stringifiedValue,
        kg: co2FootprintsVOs.merchandise.kg,
      },
      {
        color: 'var(--chart-color5)',
        label: t('events.pages.result.topContainer.marks.durationAndVenue'),
        value: co2FootprintsVOs.eventDurationAndInfo.stringifiedValue,
        kg: co2FootprintsVOs.eventDurationAndInfo.kg,
      },
    ],
    [co2FootprintsVOs, t],
  );

  return (
    <Root>
      <SectionLeft>
        <EventText>{t('events.pages.result.topContainer.event', { eventName: eventName })}</EventText>
        <RedAccentHeading>
          <Title>
            <span dangerouslySetInnerHTML={{ __html: t('events.pages.result.topContainer.title') }}></span>
            <TotalFootprintValue value={co2FootprintSumVO.kg} />
          </Title>
        </RedAccentHeading>

        {/* Hero stat cards */}
        <StatCardsRow>
          <StatCard>
            <StatValue>{formatWeightValue(totalKg)}</StatValue>
            <StatLabel>{t('events.pages.result.topContainer.subTitle')}</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{formatWeightValue(perAttendeeVO.kg)}</StatValue>
            <StatLabel>{t('events.pages.result.topContainer.perAttendee')}</StatLabel>
          </StatCard>
        </StatCardsRow>

        <MobileDonutSection co2FootprintPerAttendeVOs={co2FootprintPerAttendeVOs} />
        <ChartSection co2FootprintPerAttendeVOs={co2FootprintPerAttendeVOs} />
        <ResultsWrapper>
          <BreakdownTitle>{t('events.pages.result.topContainer.subTitle')}</BreakdownTitle>
          <BreakdownList>
            {breakdownItems.map(item => {
              const pct = totalKg > 0 ? Math.round((item.kg / totalKg) * 100) : 0;
              return (
                <BreakdownCard key={item.label}>
                  <BreakdownCardTop>
                    <BreakdownLabelRow>
                      <Mark $color={item.color} />
                      <BreakdownLabel>{item.label}</BreakdownLabel>
                    </BreakdownLabelRow>
                    <BreakdownValue>{item.value}</BreakdownValue>
                  </BreakdownCardTop>
                  <ProgressTrack>
                    <ProgressFill $color={item.color} $width={pct} />
                  </ProgressTrack>
                  <BreakdownPct>{pct}%</BreakdownPct>
                </BreakdownCard>
              );
            })}
          </BreakdownList>
        </ResultsWrapper>
      </SectionLeft>
      <SectionRight>
        <ChartDonutSection co2FootprintPerAttendeVOs={co2FootprintPerAttendeVOs} />
      </SectionRight>
    </Root>
  );
};

const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to { opacity: 1; transform: scale(1); }
`;

const Root = styled.div`
  width: 100%;
  margin-bottom: 10px;
  @media ${DEVICE.laptop} {
    margin-bottom: 30px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 40px;
  }
`;

const EventText = styled.h4`
  ${TYPOGRAPHY.bodyRegular16}
  margin-bottom: 12px;
  color: var(--color9);

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.bodyMedium20}
    margin-bottom: 16px;
  }
`;

const SectionLeft = styled.div`
  flex: 2;
`;

const SectionRight = styled.div`
  display: none;
  flex-shrink: 0;
  @media ${DEVICE.laptop} {
    display: block;
    opacity: 0;
    animation: ${scaleIn} 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.35s forwards;
  }
`;

const Title = styled.h1`
  ${TYPOGRAPHY.titleSemibold28}
  flex-direction: column;
  color: var(--color6);
  margin-bottom: 24px;

  @media ${DEVICE.mobileL} {
    flex-direction: row;
  }

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.titleSemibold44}
    margin-bottom: 36px;
  }
`;

/* --- Stat hero cards --- */
const StatCardsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 32px;
  opacity: 0;
  animation: ${fadeSlideUp} 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.2s forwards;

  @media ${DEVICE.laptop} {
    gap: 24px;
    margin-bottom: 48px;
  }
`;

const StatCard = styled.div`
  background: var(--color5);
  border: 1px solid var(--color10);
  border-radius: 16px;
  padding: 20px 16px;
  text-align: center;
  transition: box-shadow 0.3s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    transform: translateY(-2px);
  }

  @media ${DEVICE.laptop} {
    padding: 28px 24px;
  }
`;

const StatValue = styled.div`
  ${TYPOGRAPHY.titleSemibold28}
  color: var(--color2);
  margin-bottom: 4px;

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.titleSemibold36}
  }
`;

const StatLabel = styled.div`
  ${TYPOGRAPHY.captionRegular12}
  color: var(--color9);
  text-transform: uppercase;
  letter-spacing: 0.06em;

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.bodyRegular14}
  }
`;

/* --- Breakdown --- */
const ResultsWrapper = styled.div`
  margin-bottom: 40px;

  @media ${DEVICE.laptop} {
    margin-bottom: 60px;
  }
`;

const BreakdownTitle = styled.h3`
  ${TYPOGRAPHY.bodyMedium16}
  color: var(--color6);
  margin-bottom: 16px;

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.bodyMedium20}
    margin-bottom: 20px;
  }
`;

const BreakdownList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media ${DEVICE.laptop} {
    gap: 16px;
  }
`;

const BreakdownCard = styled.div`
  background: var(--color5);
  border: 1px solid var(--color10);
  border-radius: 12px;
  padding: 16px;
  max-width: 845px;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  }

  @media ${DEVICE.laptop} {
    padding: 20px 24px;
  }
`;

const BreakdownCardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const BreakdownLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Mark = styled.span<{ $color: string }>`
  width: 14px;
  height: 14px;
  border-radius: 4px;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const BreakdownLabel = styled.span`
  ${TYPOGRAPHY.bodyRegular14}
  color: var(--color6);

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.bodyRegular16}
  }
`;

const BreakdownValue = styled.span`
  ${TYPOGRAPHY.bodyMedium16}
  color: var(--color6);
  white-space: nowrap;

  @media ${DEVICE.laptop} {
    ${TYPOGRAPHY.bodyMedium20}
  }
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 6px;
  background: var(--color8);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 6px;
`;

const ProgressFill = styled.div<{ $color: string; $width: number }>`
  height: 100%;
  width: ${({ $width }) => $width}%;
  background: ${({ $color }) => $color};
  border-radius: 3px;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
`;

const BreakdownPct = styled.span`
  ${TYPOGRAPHY.captionRegular12}
  color: var(--color9);
`;

const MobileDonutSection = styled(ChartDonutSection)`
  margin-bottom: 44px;

  @media ${DEVICE.laptop} {
    display: none;
  }
`;
